import { useMemo } from 'react';
import { CommanderAPI } from '../CommanderAPI';
import useMyWorkspacesChildren from '../../../../redux/workspace/hooks/useMyWorkspacesChildren';
import useMultipleQueries from '../../../../shared/util/rtk/useQueries';
import { DeviceStatusAirConnectAPIResponse } from '../deviceStatus.model';
import { M_SECONDS_POOLING_INTERVAL_DATA } from '../../../store/Store.constants';

type AggregatedAirConnectStatus = DeviceStatusAirConnectAPIResponse & {
  workspaceId: string;
};

type AggregatedResult = {
  isUninitialized: boolean;
  isFetching: boolean;
  isLoading: boolean;
  currentData?: AggregatedAirConnectStatus[];
  data?: AggregatedAirConnectStatus[];
  error?: unknown;
};

function useAggregatedGetAirConnectStatusBySubworkspaces(
  enabled = true
): AggregatedResult {
  const { data: childWorkspacesDict } = useMyWorkspacesChildren({
    skip: !enabled,
  });

  const childWorkspaceIds = useMemo(
    () => (childWorkspacesDict ? Object.keys(childWorkspacesDict) : []),
    [childWorkspacesDict]
  );

  const childWorkspaceResponses = useMultipleQueries<
    DeviceStatusAirConnectAPIResponse[] | undefined,
    string
  >(CommanderAPI.endpoints.getAirConnectStatusByWorkspace, childWorkspaceIds, {
    pollingInterval: M_SECONDS_POOLING_INTERVAL_DATA,
    refetchOnMountOrArgChange: true,
    skip: !enabled || !childWorkspaceIds.length,
  });

  const aggregatedCurrentData = useMemo<
    AggregatedAirConnectStatus[] | undefined
  >(() => {
    if (!enabled || !childWorkspaceIds.length) {
      return [];
    }

    const lists =
      childWorkspaceResponses.currentData ?? childWorkspaceResponses.data;
    if (!lists) {
      return undefined;
    }

    return lists.flatMap((devices, index) => {
      const workspaceId = childWorkspaceIds[index];
      return (devices ?? []).map((device) => ({
        ...device,
        workspaceId,
      }));
    });
  }, [
    childWorkspaceIds,
    childWorkspaceResponses.currentData,
    childWorkspaceResponses.data,
    enabled,
  ]);

  return useMemo(
    () => ({
      ...childWorkspaceResponses,
      currentData: aggregatedCurrentData,
      data: aggregatedCurrentData,
      isUninitialized: !enabled || childWorkspaceResponses.isUninitialized,
      isLoading:
        childWorkspaceResponses.isFetching &&
        aggregatedCurrentData === undefined,
    }),
    [aggregatedCurrentData, childWorkspaceResponses, enabled]
  );
}

export default useAggregatedGetAirConnectStatusBySubworkspaces;
